import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAssistants } from '../context/AssistantContext';
import { formatErrorMessage } from '../utils/formatErrorMessage';
import { readSseStream, readApiErrorMessage } from '../utils/sseStream';
import { AssistantDropdown } from './AssistantDropdown';
import { AppLogo } from './AppLogo';
import { templates } from '../data/templates';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

export function AssistantChat() {
  const { activeAssistant, activeAssistantId } = useAssistants();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const streamAbortRef = useRef<AbortController | null>(null);

  // Reset messages + conversationId when switching assistants, and abort any
  // in-flight stream so the previous assistant's response (or its
  // conversationId) can't bleed into the new assistant's state.
  useEffect(() => {
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    setMessages([]);
    setConversationId(null);
    setInput('');
    setActionError(null);
    setIsLoading(false);
  }, [activeAssistantId]);

  // Abort any in-flight stream on unmount.
  useEffect(() => {
    return () => streamAbortRef.current?.abort();
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  const activeTemplate = activeAssistant?.template_id
    ? templates.find(t => t.id === activeAssistant.template_id)
    : null;

  const suggestedStarters = activeTemplate?.suggestedStarters ?? [
    'What information do you have?',
    'Summarize the key points',
    'What can you help me with?',
  ];

  const sendMessage = async () => {
    if (!input.trim() || isLoading || !activeAssistant) return;
    if (activeAssistant.status !== 'ready') {
      setActionError(`This assistant is not ready yet (status: ${activeAssistant.status}).`);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setActionError(null);

    const assistantId = (Date.now() + 1).toString();
    setMessages(prev => [...prev, { id: assistantId, role: 'assistant', content: '' }]);

    const controller = new AbortController();
    streamAbortRef.current = controller;

    const appendToAssistantMessage = (text: string) => {
      setMessages(prev =>
        prev.map(m => (m.id === assistantId ? { ...m, content: m.content + text } : m))
      );
    };

    try {
      const response = await fetch(`/api/v1/assistants/${activeAssistant.id}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.content,
          conversationId,
        }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(
          await readApiErrorMessage(response, 'Failed to get response. Please try again later.')
        );
      }

      let accumulatedContent = '';
      let streamErrorText: string | null = null;

      await readSseStream(response.body, (json) => {
        // Ignore late events if the user switched assistants mid-stream.
        if (controller.signal.aborted) return;

        if (typeof json.error === 'string') {
          streamErrorText = json.error;
          return;
        }

        if (typeof json.conversationId === 'string') {
          setConversationId(prev => prev ?? (json.conversationId as string));
        }

        if (typeof json.content === 'string' && json.content) {
          accumulatedContent += json.content;
          appendToAssistantMessage(json.content);
        }

        if (json.done && Array.isArray(json.citations) && json.citations.length > 0) {
          appendToAssistantMessage(`\n\n---\n*Sources: ${(json.citations as string[]).join(', ')}*`);
        }
      });

      if (streamErrorText) {
        if (accumulatedContent.trim().length === 0) {
          throw new Error(streamErrorText);
        }
        // Partial answer + server-reported failure: make the truncation
        // visible instead of letting a half-answer look complete.
        appendToAssistantMessage('\n\n---\n⚠️ *The response was interrupted by an error and may be incomplete. Please retry if needed.*');
      }
    } catch (error) {
      if (controller.signal.aborted) return; // assistant switched / unmounted

      console.error('Chat error:', error);

      let errorMessage = 'Failed to get response. Please try again later.';
      if (error instanceof Error) {
        errorMessage = error.message || errorMessage;
        if (error.message.includes('fetch') || error.message.includes('network')) {
          errorMessage = 'Unable to connect to the server. Please check your connection and try again.';
        } else if (error.message.includes('API key')) {
          errorMessage = 'Server configuration error. Please contact support.';
        }
      }

      errorMessage = formatErrorMessage(errorMessage);

      setMessages(prev =>
        prev.map(m =>
          m.id === assistantId
            ? { ...m, content: errorMessage }
            : m
        )
      );
    } finally {
      if (streamAbortRef.current === controller) {
        streamAbortRef.current = null;
        setIsLoading(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearConversation = async () => {
    if (!activeAssistant) return;
    setActionError(null);
    if (conversationId) {
      try {
        const res = await fetch(`/api/v1/assistants/${activeAssistant.id}/chat/clear`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conversationId }),
        });
        if (!res.ok) throw new Error(await readApiErrorMessage(res, 'Failed to clear the conversation.'));
      } catch (err) {
        console.error('Failed to clear conversation:', err);
        // Don't pretend it worked — the server-side context still exists.
        setActionError('Could not clear the conversation on the server. Please try again.');
        return;
      }
    }
    setMessages([]);
    setConversationId(null);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] min-h-[500px] bg-white border border-neutral-200 rounded-card shadow-card overflow-hidden">
      {/* Header with dropdown + clear button */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-200 bg-neutral-50">
        <div className="flex-1 max-w-xs">
          <AssistantDropdown />
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearConversation}
            className="text-xs text-neutral-500 hover:text-neutral-700 px-2 py-1 rounded hover:bg-neutral-100 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Messages area */}
      <div
        ref={messagesContainerRef}
        className={`flex-1 p-4 space-y-4 ${messages.length === 0 ? 'overflow-y-hidden' : 'overflow-y-auto overscroll-contain'}`}
      >
        {!activeAssistant ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-14 h-14 bg-neutral-100 rounded-full flex items-center justify-center mb-4 ring-1 ring-neutral-200">
              <AppLogo sizeClassName="w-10 h-10" className="rounded-full opacity-90" />
            </div>
            <h2 className="text-lg font-semibold text-neutral-700 mb-2 font-heading">
              Select Assistant To Start Chatting
            </h2>
            <p className="text-neutral-400 text-sm max-w-sm">
              Choose a trained assistant from the dropdown above. You can create assistants in the Assistants tab.
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-14 h-14 bg-primary-50 rounded-full flex items-center justify-center mb-4 ring-1 ring-primary-100">
              <AppLogo sizeClassName="w-10 h-10" className="rounded-full" />
            </div>
            <h2 className="text-lg font-semibold text-neutral-800 mb-1 font-heading">
              {activeAssistant.name}
            </h2>
            {activeTemplate && (
              <p className="text-neutral-500 text-sm mb-4">{activeTemplate.description}</p>
            )}
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {suggestedStarters.map((starter, i) => (
                <button
                  key={i}
                  onClick={() => setInput(starter)}
                  className="text-sm px-3 py-1.5 bg-neutral-100 text-neutral-600 rounded-pill hover:bg-neutral-200 hover:shadow-card hover:-translate-y-px transition-all"
                >
                  {starter}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map(message => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'assistant' ? (
                <div className="max-w-2xl w-full">
                  <div className="bg-neutral-100 rounded-2xl px-4 py-3 text-sm leading-relaxed text-neutral-800">
                    {message.content === '' && isLoading ? (
                      <div className="inline-flex gap-1">
                        <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({ href, children }) => (
                            <a
                              href={href}
                              className="text-primary-600 hover:text-primary-700 underline decoration-primary-300 underline-offset-2"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {children}
                            </a>
                          ),
                          h3: ({ children }) => <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2 font-heading">{children}</h3>,
                          h4: ({ children }) => <h4 className="text-sm font-medium text-neutral-700 mt-3 mb-1 font-heading">{children}</h4>,
                          ul: ({ children }) => <ul className="list-disc list-inside space-y-1 my-3 pl-1">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 my-3 pl-1">{children}</ol>,
                          li: ({ children, ...props }) => (
                            <li className="text-neutral-700 leading-relaxed [&>p]:mb-0 [&>p]:mt-0 [&>p:first-child]:inline" {...props}>
                              {children}
                            </li>
                          ),
                          p: ({ children, ...props }) => <p className="mb-3 leading-relaxed last:mb-0" {...props}>{children}</p>,
                          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                          hr: () => <hr className="my-4 border-neutral-200" />,
                          em: ({ children }) => <em className="italic text-neutral-600">{children}</em>,
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    )}
                  </div>
                </div>
              ) : (
                <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-primary-600 text-white">
                  <p className="text-sm">{message.content}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Input area */}
      <div className="border-t border-neutral-200 p-4">
        {actionError && (
          <p className="text-xs text-red-500 mb-2" role="alert">{actionError}</p>
        )}
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={activeAssistant ? 'Ask your assistant...' : 'Select An Assistant First'}
            aria-label="Chat message"
            rows={1}
            disabled={isLoading || !activeAssistant}
            className="flex-1 resize-none rounded-btn border border-neutral-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading || !activeAssistant}
            aria-label="Send message"
            className="btn-primary p-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        <p className="text-xs text-neutral-500 mt-2 text-center">
          Responses are based on your uploaded documents. Always verify clinical information with authoritative sources.
        </p>
      </div>
    </div>
  );
}
